import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiMessageCircle, FiPhone, FiInstagram, FiFacebook, FiMapPin } = FiIcons;

const SocialProof = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const reviews = [
    {
      name: "Sarah M.",
      country: "Australia",
      rating: 5,
      text: "The natural spring pool is absolutely magical! Best budget accommodation we found in Sri Lanka. The peacocks waking us up was a delightful surprise.",
      platform: "Booking.com"
    },
    {
      name: "Marco R.",
      country: "Germany",
      rating: 5,
      text: "Perfect location for Sinharaja exploration. The river access is incredible and the food is authentic village cooking. Highly recommended!",
      platform: "Agoda"
    },
    {
      name: "Priya S.",
      country: "India",
      rating: 5,
      text: "Stayed here for our family reunion. The whole villa rental was perfect for 12 people. Kids loved the natural pool and we adults enjoyed the peace.",
      platform: "Direct Booking"
    }
  ];

  const stats = [
    { number: "9.4", label: "Guest Rating", sublabel: "/10" },
    { number: "95%", label: "Would Return", sublabel: "Guest Survey" },
    { number: "3min", label: "To River", sublabel: "Walking Distance" },
    { number: "$17", label: "Starting Price", sublabel: "Per Night" }
  ];

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent("Hi! I'd like to make a reservation at Villa Springwater. Can you help me with availability and booking?");
    window.open(`https://wa.me/94704003478?text=${message}`, '_blank');
  };

  const handlePhoneContact = () => {
    window.open('tel:+94704003478', '_self');
  };

  return (
    <section id="social-proof" className="relative py-20 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-r from-emerald-500/20 to-teal-400/20 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
          transition={{ duration: 20, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-blue-500/20 to-purple-400/20 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], rotate: [360, 180, 0] }}
          transition={{ duration: 25, repeat: Infinity }}
        />
      </div>

      <div ref={ref} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats Section */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="text-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 * index }}
            >
              <div className="glass-morphism rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
                <motion.div
                  className="text-3xl md:text-4xl font-bold gradient-primary bg-clip-text text-transparent mb-2"
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                >
                  {stat.number}
                  <span className="text-lg text-gray-400">{stat.sublabel}</span>
                </motion.div>
                <div className="text-white font-semibold">{stat.label}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Reviews Section */}
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-12 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            What Our
            <span className="gradient-primary bg-clip-text text-transparent ml-3">
              Guests Say
            </span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={index}
                className="glass-morphism rounded-2xl p-6"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.2 }}
                whileHover={{ scale: 1.02 }}
              >
                {/* Rating */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="text-yellow-400 text-lg" />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "{review.text}"
                </p>

                {/* Reviewer Info */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold">{review.name}</div>
                    <div className="text-gray-400 text-sm">{review.country}</div>
                  </div>
                  <div className="text-emerald-400 text-sm font-medium">
                    {review.platform}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA Section */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          <div className="glass-morphism rounded-3xl p-8 md:p-12 max-w-4xl mx-auto">
            <motion.h3
              className="text-3xl md:text-4xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.6 }}
            >
              Ready to Experience
              <span className="gradient-primary bg-clip-text text-transparent ml-2">
                Villa Springwater?
              </span>
            </motion.h3>

            <motion.p
              className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.8 }}
            >
              Book direct and save! Skip the OTA commissions and get the best rates available. Your rainforest adventure is just one message away.
            </motion.p>

            {/* Contact Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 2 }}
            >
              <motion.button
                onClick={handleWhatsAppContact}
                className="gradient-primary text-white px-8 py-4 rounded-2xl font-semibold text-lg flex items-center gap-3 shadow-xl hover:shadow-2xl transition-all duration-300 min-w-[280px]"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiMessageCircle} className="text-xl" />
                Book Now via WhatsApp
              </motion.button>

              <motion.button
                onClick={handlePhoneContact}
                className="glass-dark text-white px-8 py-4 rounded-2xl font-semibold text-lg flex items-center gap-3 hover:bg-white/20 transition-all duration-300 min-w-[280px]"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiPhone} className="text-xl" />
                Call +94 70 400 3478
              </motion.button>
            </motion.div>

            {/* Contact Details */}
            <motion.div
              className="border-t border-white/20 pt-8"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 2.2 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center mb-8">
                {/* Address */}
                <div className="flex flex-col items-center gap-2">
                  <SafeIcon icon={FiMapPin} className="text-emerald-400 text-xl" />
                  <div>
                    <div className="text-white font-semibold">Address</div>
                    <div className="text-gray-300 text-sm">No. 2/1 Andadola, Dellawa, Warukandeniya</div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiInstagram} className="text-emerald-400 text-xl" />
                    <SafeIcon icon={FiFacebook} className="text-emerald-400 text-xl" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">Follow Us</div>
                    <div className="text-gray-300 text-sm">@villaspringwater</div>
                  </div>
                </div>

                {/* Best Price Guarantee */}
                <div className="flex flex-col items-center gap-2">
                  <SafeIcon icon={FiStar} className="text-emerald-400 text-xl" />
                  <div>
                    <div className="text-white font-semibold">Best Price Guarantee</div>
                    <div className="text-gray-300 text-sm">Book direct & save</div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-white/10 pt-6 text-center">
                <p className="text-gray-400 text-sm">
                  © 2025 Villa Springwater. Designed and Created by{' '}
                  <a 
                    href="https://github.com/RajanthaR" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-emerald-400 hover:text-emerald-300 transition-colors"
                  >
                    Rajantha
                  </a>
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SocialProof;